import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { NamedLink } from '../../../components';

import css from './HeroPanel.module.css';

/**
 * HeroPanel component that displays the hero section with title, subtitle, and CTA
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class
 * @param {string} [props.rootClassName] - Custom class that overrides the default class
 * @param {string} [props.title] - Main hero title (e.g., "Make room for magic")
 * @param {string} [props.subtitle] - Subtitle text (e.g., "Kick off your selling adventure...")
 * @param {string} [props.ctaText] - Call-to-action message for logged out users (e.g., "Ready to get your repoze shop set up?")
 * @param {string} [props.ctaButtonText] - Button text (e.g., "Sell Now")
 * @param {string} [props.ctaButtonLink] - Button link destination
 * @param {boolean} [props.isAuthenticated] - Whether the user is logged in
 * @returns {JSX.Element} Hero panel with title, subtitle, and CTA button
 */
const HeroPanel = props => {
  const {
    className,
    rootClassName,
    title = 'Make room\nfor magic',
    subtitle = 'Kick off your selling adventure and list products in seconds!',
    ctaText = 'Ready to get your repoze shop set up?',
    ctaButtonText = 'Sell now',
    ctaButtonLink = '/l/new',
    isAuthenticated = false,
  } = props;

  // The server always renders signed out (auth info loads only in the browser), so the first
  // client render must use the signed-out text too, or hydration fails. Switch after mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Show different CTA text based on authentication status
  const displayCtaText = isAuthenticated && mounted ? 'Ready to list an item?' : ctaText;

  const classes = classNames(rootClassName || css.root, className);

  // Split title by newline for proper rendering
  const titleLines = title.split('\n');

  return (
    <div className={classes}>
      <div className={css.content}>
        {/* Main Title */}
        <h1 className={css.title}>
          {titleLines.map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index < titleLines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h1>

        {/* Subtitle */}
        <p className={css.subtitle}>{subtitle}</p>

        {/* CTA Container */}
        <div className={css.ctaContainer}>
          <span className={css.ctaText}>{displayCtaText}</span>
          <NamedLink
            name="NewListingPage"
            className={css.ctaButton}
          >
            {ctaButtonText}
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

HeroPanel.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  ctaText: PropTypes.string,
  ctaButtonText: PropTypes.string,
  ctaButtonLink: PropTypes.string,
  isAuthenticated: PropTypes.bool,
};

export default HeroPanel;

